import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import localStorageService from '../../utils/localStorageService';
import { motion } from 'framer-motion';
import {
  Bars3Icon,
  BellIcon,
  ChevronDownIcon,
  MoonIcon,
  SunIcon,
  LanguageIcon,
  AcademicCapIcon,
  MagnifyingGlassIcon,
} from '@heroicons/react/24/outline';
import { useLanguage } from '../../contexts/LanguageContext';
import { useTheme } from '../../contexts/ThemeContext';

const Header = ({ toggleMobileSidebar, toggleDesktopSidebar }) => {
  const { language, setLanguage } = useLanguage();
  const { isDark, toggleTheme } = useTheme();
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [notificationsOpen, setNotificationsOpen] = useState(false);

  const toggleUserMenu = () => {
    setUserMenuOpen(!userMenuOpen);
    setNotificationsOpen(false);
  };

  const toggleNotifications = () => {
    setNotificationsOpen(!notificationsOpen);
    setUserMenuOpen(false);
  };

  const getCurrentUser = () => {
    const users = localStorageService.getAllItems('users') || [];
    const currentUserId = localStorage.getItem('currentUserId');
    const currentUser = users.find((u) => u.id === currentUserId);

    return (
      currentUser || {
        name: 'Utilisateur',
        role: 'Administrateur',
        avatar: null,
      }
    );
  };

  const getRealNotifications = () => {
    return localStorageService.getAllItems('notifications') || [];
  };

  const currentUser = getCurrentUser();
  const notifications = getRealNotifications();

  const panelMotion = {
    initial: { opacity: 0, y: -6 },
    animate: { opacity: 1, y: 0 },
  };

  const menuButton =
    'rounded-lg p-2 text-slate-600 transition hover:bg-slate-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-forma-500 dark:text-slate-400 dark:hover:bg-slate-800';

  return (
    <header className="sticky top-0 z-30 border-b border-slate-200/90 bg-white/90 backdrop-blur-xl dark:border-slate-800 dark:bg-slate-950/85">
      <div className="px-4 sm:px-6 lg:px-8">
        <div className="flex h-14 items-center justify-between sm:h-16">
          <div className="flex min-w-0 flex-1 items-center gap-2">
            <button
              type="button"
              className={`${menuButton} lg:hidden`}
              onClick={toggleMobileSidebar}
            >
              <span className="sr-only">Ouvrir le menu</span>
              <Bars3Icon className="h-6 w-6" aria-hidden="true" />
            </button>

            <button
              type="button"
              className={`${menuButton} hidden lg:inline-flex`}
              onClick={toggleDesktopSidebar}
            >
              <span className="sr-only">Basculer le menu</span>
              <Bars3Icon className="h-6 w-6" aria-hidden="true" />
            </button>

            <Link to="/dashboard" className="ml-1 hidden min-w-0 items-center gap-2.5 sm:flex">
              <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-br from-forma-500 to-forma-700 text-white shadow-soft">
                <AcademicCapIcon className="h-5 w-5" aria-hidden />
              </div>
              <div className="flex flex-col leading-tight">
                <span className="text-sm font-semibold tracking-tight text-slate-900 dark:text-white">
                  FormaPro
                </span>
                <span className="text-xs font-medium text-forma-700 dark:text-forma-400">
                  École de formation
                </span>
              </div>
            </Link>

            <div className="ml-2 hidden min-w-0 flex-1 md:ml-8 md:block md:max-w-md lg:max-w-lg">
              <label className="relative block">
                <span className="sr-only">Rechercher</span>
                <MagnifyingGlassIcon
                  className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400 dark:text-slate-500"
                  aria-hidden
                />
                <input
                  type="search"
                  placeholder="Rechercher une page, un cours, un étudiant…"
                  className="w-full rounded-xl border border-slate-200 bg-slate-50 py-2 pl-10 pr-3 text-sm text-slate-900 placeholder:text-slate-400 focus:border-forma-500 focus:outline-none focus:ring-2 focus:ring-forma-500/20 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100 dark:placeholder:text-slate-500 dark:focus:border-forma-400"
                />
              </label>
            </div>
          </div>

          <div className="flex shrink-0 items-center gap-1 sm:gap-2">
            <button
              type="button"
              className={menuButton}
              onClick={() => toggleTheme()}
              aria-label={isDark ? 'Passer au thème clair' : 'Passer au thème sombre'}
              aria-pressed={isDark}
            >
              {isDark ? (
                <SunIcon className="h-5 w-5" aria-hidden="true" />
              ) : (
                <MoonIcon className="h-5 w-5" aria-hidden="true" />
              )}
            </button>

            <button
              type="button"
              className={menuButton}
              onClick={() => setLanguage(language === 'fr' ? 'ar' : 'fr')}
              aria-label="Changer la langue"
            >
              <LanguageIcon className="h-5 w-5" aria-hidden="true" />
            </button>

            <div className="relative">
              <button type="button" className={menuButton} onClick={toggleNotifications}>
                <span className="sr-only">Notifications</span>
                <BellIcon className="h-5 w-5" aria-hidden="true" />
                {notifications.length > 0 && (
                  <span className="absolute right-1 top-1 h-2 w-2 rounded-full bg-danger-500 ring-2 ring-white dark:ring-slate-950" />
                )}
              </button>

              {notificationsOpen && (
                <>
                  <button
                    type="button"
                    className="fixed inset-0 z-10 cursor-default bg-slate-950/20 dark:bg-black/40"
                    aria-label="Fermer les notifications"
                    onClick={() => setNotificationsOpen(false)}
                  />
                  <motion.div
                    {...panelMotion}
                    className="absolute right-0 z-20 mt-2 w-80 origin-top-right overflow-hidden rounded-xl border border-slate-200 bg-white shadow-medium dark:border-slate-700 dark:bg-slate-900"
                  >
                    <div className="border-b border-slate-100 px-4 py-3 dark:border-slate-800">
                      <h3 className="text-sm font-semibold text-slate-900 dark:text-white">
                        Notifications
                      </h3>
                    </div>
                    <div className="max-h-64 overflow-y-auto main-scroll">
                      {notifications.length === 0 ? (
                        <p className="px-4 py-8 text-center text-sm text-slate-500 dark:text-slate-400">
                          Aucune notification
                        </p>
                      ) : (
                        <ul className="divide-y divide-slate-100 dark:divide-slate-800">
                          {notifications.map((notification) => (
                            <li
                              key={notification.id}
                              className={`px-4 py-3 text-sm hover:bg-slate-50 dark:hover:bg-slate-800/80 ${
                                !notification.read ? 'bg-forma-50/60 dark:bg-forma-950/20' : ''
                              }`}
                            >
                              <div className="flex justify-between gap-2">
                                <span className="font-medium text-slate-900 dark:text-white">
                                  {notification.title}
                                </span>
                                <span className="text-xs text-slate-500 dark:text-slate-400">
                                  {notification.time}
                                </span>
                              </div>
                              <p className="mt-1 text-xs text-slate-600 dark:text-slate-400">
                                {notification.message}
                              </p>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  </motion.div>
                </>
              )}
            </div>

            <div className="relative">
              <button
                type="button"
                className="ml-1 flex items-center gap-2 rounded-xl border border-transparent p-1.5 pr-2 text-left transition hover:bg-slate-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-forma-500 dark:hover:bg-slate-800"
                onClick={toggleUserMenu}
              >
                <span className="sr-only">Menu utilisateur</span>
                {currentUser.avatar ? (
                  <img className="h-8 w-8 rounded-full ring-2 ring-slate-200 dark:ring-slate-700" src={currentUser.avatar} alt="" />
                ) : (
                  <div className="flex h-8 w-8 items-center justify-center rounded-full bg-gradient-to-br from-forma-500 to-forma-700 text-xs font-bold text-white shadow-sm">
                    {currentUser.name.charAt(0)}
                  </div>
                )}
                <span className="hidden max-w-[8rem] truncate text-sm font-medium text-slate-800 dark:text-slate-100 sm:block">
                  {currentUser.name}
                </span>
                <ChevronDownIcon className="hidden h-4 w-4 text-slate-400 sm:block" aria-hidden="true" />
              </button>

              {userMenuOpen && (
                <>
                  <button
                    type="button"
                    className="fixed inset-0 z-10 cursor-default bg-slate-950/20 dark:bg-black/40"
                    aria-label="Fermer le menu utilisateur"
                    onClick={() => setUserMenuOpen(false)}
                  />
                  <motion.div
                    {...panelMotion}
                    className="absolute right-0 z-20 mt-2 w-52 origin-top-right overflow-hidden rounded-xl border border-slate-200 bg-white py-1 shadow-medium dark:border-slate-700 dark:bg-slate-900"
                  >
                    <div className="border-b border-slate-100 px-4 py-3 dark:border-slate-800">
                      <p className="truncate text-sm font-semibold text-slate-900 dark:text-white">
                        {currentUser.name}
                      </p>
                      <p className="truncate text-xs text-slate-500 dark:text-slate-400">{currentUser.role}</p>
                    </div>
                    <Link
                      to="/settings"
                      className="block px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50 dark:text-slate-200 dark:hover:bg-slate-800"
                      onClick={() => setUserMenuOpen(false)}
                    >
                      Paramètres
                    </Link>
                    <Link
                      to="/login"
                      className="block px-4 py-2.5 text-sm text-danger-600 hover:bg-danger-50 dark:text-danger-400 dark:hover:bg-danger-950/30"
                      onClick={() => setUserMenuOpen(false)}
                    >
                      Déconnexion
                    </Link>
                  </motion.div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
